import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, Input, input, OnDestroy, OnInit, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';

import { filter, Observable, Subscriber } from 'rxjs';

import { BaasEditLoader } from '../../baas-edit/loader/baas-edit-loader';
import { BaasRemarkList } from '../../remarks/baas-remark-list/baas-remark-list';
import { BarEditPageService } from '../bar-edit-page.service';
import { BarEditPageLoadArgs } from '../bar-edit-page-load-args.model';
import { BarEditPageLoadReason } from '../bar-edit-page-load-reason.enum';
import { BarEditPageSaveArgs } from '../bar-edit-page-save-args.model';
import { BarEditPageBaseComponent } from '../core/bar-edit-page-base.component';

@Component({
    templateUrl: './bar-edit-page-wrapper.component.html',
    selector: 'bar-edit-page-wrapper',
    // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [NgTemplateOutlet, ReactiveFormsModule, BaasEditLoader, BaasRemarkList]
})
export class BarEditPageWrapperComponent extends BarEditPageBaseComponent<any, FormGroup> implements OnInit, OnDestroy {
    readonly #destroyRef = inject(DestroyRef);
    readonly #editPageService = inject(BarEditPageService, { optional: true });

    #data: any;
    #saveArgs: { subscriber: Subscriber<any | boolean | void>; args: BarEditPageSaveArgs<any> };
    #deleteArgs: { subscriber: Subscriber<boolean | void> };
    #preventSubmittedEvents = false;
    /** Allows to run something before saving and to prevent the save from happen. */
    readonly beforeSaving = input<() => void | boolean | Observable<void | boolean>>(undefined);
    /** Occurs when saving. Here we need to persist the data. */
    readonly saving = input<(model: any) => Observable<any | boolean | void>>(undefined);
    /** Callback indicating whether save was completed successfully at the consumer. */
    readonly saveSuccess$ = input<Observable<any | boolean | void>>(undefined);
    /** Callback indicating whether save failed due to an error at the consumer. */
    readonly saveFailed$ = input<Observable<Error>>(undefined);
    /** Callback indicating whether delete was completed successfully by the consumer. */
    readonly deleteSuccess$ = input<Observable<boolean | void>>(undefined);
    /** Callback indicating whether delete failed due to an error by the consumer. */
    readonly deleteFailed$ = input<Observable<Error>>(undefined);
    /** Load occurs whenever data needs to be loaded (first time, after save, after undo...) */
    readonly loading = input<(args: BarEditPageLoadArgs) => void | Observable<void>>(undefined);
    /** Performs the delete. */
    readonly deleting = input<() => Observable<boolean | void>>(undefined);
    /** Performs extra undo functionality. */
    readonly undoing = input<() => Observable<void> | void>(undefined);
    /** Determines if loading progress bar must appears in case no model is uses (modelless modus). */
    @Input() showLoading: boolean = undefined;
    /** Sets the data retrieved from back-end. */
    @Input() set data(value: any) {
        // invoke the load method
        if (value) {
            // call the onload method again in case first time or if the reference has changed
            this.load(value, true, BarEditPageLoadReason.Load, this.#data == null);
            this.#data = value;
        }
    }
    get data() {
        return this.#data;
    }
    @Input() set editForm(value: FormGroup) {
        this.form = value;
    }
    get editForm() {
        return this.form;
    }
    /** Observable with the model. */
    readonly model$ = input<Observable<any>>(undefined);
    /** Event that occurs that a save needs to occur at the consumer after validation checks. */
    readonly saveRequested = output<BarEditPageSaveArgs<any>>();
    /** Occurs when the save is completed and has been executed. Some code can be executed at the consumer. */
    readonly saveCompleted = output<BarEditPageSaveArgs<any>>();
    /** Event that occurs that a delete needs to occur at the consumer. */
    readonly deleteRequested = output<void>();
    /** Event that occurs when an undo is requested. Use this to perform custom undo logic. */
    readonly undoRequested = output<void>();
    /** Event that occurs when the save button is pressed to indicate that the user submitted the form. */
    readonly submittedChange = output<boolean>();
    /** Event that occurs when the edit button (pencil) is pressed. Use this to perform extra logic. */
    readonly editRequested = output<void>();

    constructor() {
        super();

        this.#hookInEditPageService();
    }

    ngOnInit(): void {
        super.ngOnInit();

        const saveSuccess$ = this.saveSuccess$();
        if (saveSuccess$) {
            saveSuccess$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((value) => {
                if (this.#saveArgs?.subscriber) {
                    this.saveCompleted.emit(this.#saveArgs.args);
                    this.#saveArgs.subscriber.next(value);
                    this.#saveArgs.subscriber.complete();
                    this.#saveArgs = undefined;
                }
            });
        }
        const saveFailed$ = this.saveFailed$();
        if (saveFailed$) {
            saveFailed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((error) => {
                if (this.#saveArgs?.subscriber) {
                    this.#saveArgs.subscriber.error(error);
                    this.#saveArgs.subscriber = undefined;
                    this.#saveArgs = undefined;
                }
            });
        }
        const deleteSuccess$ = this.deleteSuccess$();
        if (deleteSuccess$) {
            deleteSuccess$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((value) => {
                if (this.#deleteArgs?.subscriber) {
                    this.#deleteArgs.subscriber.next(value);
                    this.#deleteArgs.subscriber.complete();
                    this.#deleteArgs = undefined;
                }
            });
        }
        const deleteFailed$ = this.deleteFailed$();
        if (deleteFailed$) {
            deleteFailed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((error) => {
                if (this.#deleteArgs?.subscriber) {
                    this.#deleteArgs.subscriber.error(error);
                    this.#deleteArgs.subscriber = undefined;
                    this.#deleteArgs = undefined;
                }
            });
        }

        const model$ = this.model$();
        if (model$) {
            model$
                .pipe(
                    filter((model) => model != null),
                    takeUntilDestroyed(this.#destroyRef)
                )
                .subscribe((model) => {
                    // whenever model changes invoke the load
                    super.load(model);
                });
        }
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        if (this.#deleteArgs?.subscriber && !this.#deleteArgs.subscriber.closed) {
            this.#deleteArgs.subscriber.complete();
        }
        if (this.#saveArgs?.subscriber && !this.#saveArgs.subscriber.closed) {
            this.#saveArgs.subscriber.complete();
        }
    }

    setSubmitted(submitted: boolean) {
        this.#preventSubmittedEvents = true;
        this.submitted = submitted;
    }

    protected onLoad(args: BarEditPageLoadArgs): void {
        if (this.loading()) {
            // avoid value has changed after it was checked error
            window.setTimeout(() => {
                this.loading()(args);
            });
        }
    }

    protected onSaving(): void | boolean | Observable<void | boolean> {
        const beforeSaving = this.beforeSaving();
        if (beforeSaving) return beforeSaving();
    }

    protected onUndo(): Observable<void> | void {
        const undoing = this.undoing();
        if (undoing) return undoing();
        this.undoRequested.emit();
    }

    protected onSave(args: BarEditPageSaveArgs<any>): Observable<any | boolean | void> {
        // classic modus input callback with observable result
        const saving = this.saving();
        if (saving) return saving(args);
        // asynchronous mode (ngrx)
        // the consumer returns a void meaning he wants to work asynchronously because we expect observable in classic mode
        return new Observable<any | boolean | void>((subscriber) => {
            // store the subscriber which will be completed elsewhere (asynchronously)
            this.#saveArgs = { subscriber, args };
            this.saveRequested.emit(args);
        });
    }

    protected onDelete(): Observable<boolean | void> {
        // classic modus input callback with observable result
        const deleting = this.deleting();
        if (deleting) return deleting();
        // asynchronous mode (ngrx)
        // the consumer returns a void meaning he wants to work asynchronously because we expect observable in classic mode
        return new Observable<boolean | void>((subscriber) => {
            this.#deleteArgs = { subscriber };
            this.deleteRequested.emit();
        });
    }

    protected onEdit(): void {
        this.editRequested.emit();
    }

    protected _onSubmitted(submitted: boolean) {
        if (!this.#preventSubmittedEvents) this.submittedChange.emit(submitted);
        this.#preventSubmittedEvents = false;
    }

    /*** Hooks in the edit page service which is registered on the root component containing the bar-edit-page component. */
    #hookInEditPageService(): void {
        if (!this.#editPageService) return;

        this.#editPageService._load$.subscribe((data) => {
            const load$ = this.load(
                data.model,
                data.options?.loadModel || true,
                data.options?.reason || BarEditPageLoadReason.Load,
                data.options?.callOnLoad || true
            );
            const complete = () => {
                data.subscriber.next();
                data.subscriber.complete();
            };
            // check that onLoad returns an observable
            if (load$ !== undefined) {
                (load$ as Observable<void>).subscribe(() => {
                    // complete invoker
                    complete();
                });
            } else {
                complete();
            }
        });

        this.#editPageService._requestSave$.subscribe((data) => {
            this.requestSave(data.options).subscribe((outcome) => {
                data.subscriber.next(outcome);
                data.subscriber.complete();
            });
        });

        this.#editPageService._undo$.subscribe((data) => {
            this.undo().subscribe(() => {
                data.subscriber.next();
                data.subscriber.complete();
            });
        });

        this.#editPageService._delete$.subscribe((data) => {
            this.delete().subscribe((outcome) => {
                data.subscriber.next(outcome);
                data.subscriber.complete();
            });
        });
    }
}
